const PRETTY_PORTAL_ZOOM_PRESETS = {
  "exhibition-intro": {
    triggerSelector: "[data-zoom-trigger]",
    srcDataset: "zoomSrc",
    thumbDataset: "zoomThumb",
    altDataset: "zoomAlt",
    thumbnailClass: "pp-exhibition-intro__zoom-thumbnail",
    imageLoadSelector:
      ".pp-exhibition-intro__image, .pp-exhibition-intro__gallery-image",
    loadingAttribute: "data-exhibition-intro-loading",
  },
  "artist-media": {
    triggerSelector: "[data-artist-media-zoom-trigger]",
    srcDataset: "zoomSrc",
    thumbDataset: "zoomThumb",
    altDataset: "zoomAlt",
    thumbnailClass: "pp-artist-media__zoom-thumbnail",
    imageLoadSelector: ".pp-artist-media__gallery-image",
    loadingAttribute: "data-artist-media-loading",
  },
  "editionen-content": {
    triggerSelector: "[data-editionen-zoom-trigger]",
    srcDataset: "editionenZoomSrc",
    thumbDataset: "editionenZoomThumb",
    altDataset: "editionenZoomAlt",
    thumbnailClass: "pp-editionen-content__zoom-thumbnail",
    imageLoadSelector: ".pp-editionen-content__gallery-image",
    loadingAttribute: "data-editionen-content-loading",
  },
  "editionen-products": {
    triggerSelector: "[data-editionen-product-zoom-trigger]",
    srcDataset: "editionenProductZoomSrc",
    thumbDataset: "editionenProductZoomThumb",
    altDataset: "editionenProductZoomAlt",
    thumbnailClass: "pp-editionen-products__zoom-thumbnail",
    imageLoadSelector: ".pp-editionen-products__image",
    loadingAttribute: "data-editionen-products-loading",
  },
  "pretty-wall-detail": {
    triggerSelector: "[data-pretty-wall-zoom-trigger]",
    srcDataset: "prettyWallZoomSrc",
    typeDataset: "prettyWallZoomType",
    videoSrcDataset: "prettyWallZoomVideoSrc",
    iframeSrcDataset: "prettyWallZoomIframeSrc",
    thumbDataset: "prettyWallZoomThumb",
    altDataset: "prettyWallZoomAlt",
    thumbnailClass: "pp-pretty-wall-detail__zoom-thumbnail",
    imageLoadSelector:
      ".pp-pretty-wall-detail__gallery-image, .pp-pretty-wall-detail__hero-image",
    loadingAttribute: "data-pretty-wall-detail-loading",
  },
  "image-collage": {
    triggerSelector: "[data-image-collage-zoom-trigger]",
    srcDataset: "imageCollageZoomSrc",
    thumbDataset: "imageCollageZoomThumb",
    altDataset: "imageCollageZoomAlt",
    thumbnailClass: "image-collage__zoom-thumbnail",
    imageLoadSelector: ".image-collage__img",
    loadingAttribute: "data-image-collage-loading",
  },
  "featured-product": {
    triggerSelector: "[data-featured-product-zoom-trigger]",
    srcDataset: "featuredProductZoomSrc",
    thumbDataset: "featuredProductZoomThumb",
    altDataset: "featuredProductZoomAlt",
    thumbnailClass: "pp-featured-product__zoom-thumbnail",
    imageLoadSelector: ".pp-featured-product__image",
    loadingAttribute: "data-featured-product-loading",
  },
  "tour-booking": {
    triggerSelector: "[data-tour-media-zoom-trigger]",
    srcDataset: "tourMediaZoomSrc",
    thumbDataset: "tourMediaZoomThumb",
    altDataset: "tourMediaZoomAlt",
    thumbnailClass: "pp-tour-booking__zoom-thumbnail",
    imageLoadSelector: ".pp-tour-booking__media-asset",
    loadingAttribute: "data-tour-booking-loading",
  },
};

class PrettyPortalZoomDialog extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.abortController = new AbortController();
    this.preset =
      PRETTY_PORTAL_ZOOM_PRESETS[this.dataset.ppZoomPreset || ""] || {};
    this.dialog = this.querySelector("[data-pp-zoom-dialog]");
    this.image = this.dialog?.querySelector("[data-pp-zoom-image]");
    this.video = this.dialog?.querySelector("[data-pp-zoom-video]");
    this.iframe = this.dialog?.querySelector("[data-pp-zoom-iframe]");
    this.closeButton = this.dialog?.querySelector("[data-pp-zoom-close]");
    this.previousButton = this.dialog?.querySelector("[data-pp-zoom-prev]");
    this.nextButton = this.dialog?.querySelector("[data-pp-zoom-next]");
    this.thumbnails = this.dialog?.querySelector("[data-pp-zoom-thumbnails]");
    this.triggers = Array.from(
      this.querySelectorAll(
        this.preset.triggerSelector || "[data-pp-zoom-trigger]",
      ),
    );
    this.items = this.getItems();
    this.activeTrigger = null;
    this.activeIndex = 0;
    this.loadToken = 0;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.dragDeltaX = 0;
    this.didDrag = false;
    this.suppressClick = false;
    this.transitionDirection = 0;
    this.transitionTimer = null;

    this.markInlineImagesLoaded();
    this.renderThumbnails();
    this.updateControls();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.initialized = false;
  }

  getItems() {
    const itemIndexBySrc = new Map();
    const items = [];

    this.triggers.forEach((trigger) => {
      const src = trigger.dataset[this.preset.srcDataset || "zoomSrc"];
      const videoSrc =
        trigger.dataset[this.preset.videoSrcDataset || "zoomVideoSrc"];
      const iframeSrc =
        trigger.dataset[this.preset.iframeSrcDataset || "zoomIframeSrc"];
      const type =
        trigger.dataset[this.preset.typeDataset || "zoomType"] || "image";
      const itemSrc = src || videoSrc || iframeSrc;
      if (!itemSrc) return;

      if (itemIndexBySrc.has(itemSrc)) {
        trigger.dataset.ppZoomItemIndex = String(itemIndexBySrc.get(itemSrc));
        return;
      }

      const index = items.length;
      itemIndexBySrc.set(itemSrc, index);
      trigger.dataset.ppZoomItemIndex = String(index);
      items.push({
        trigger,
        type,
        src,
        videoSrc,
        iframeSrc,
        thumb:
          trigger.dataset[this.preset.thumbDataset || "zoomThumb"] || src || "",
        alt: trigger.dataset[this.preset.altDataset || "zoomAlt"] || "",
      });
    });

    return items;
  }

  markInlineImagesLoaded() {
    if (!this.preset.imageLoadSelector) return;

    this.querySelectorAll(this.preset.imageLoadSelector).forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;

      const markLoaded = () => {
        if (this.preset.loadingAttribute) {
          image.removeAttribute(this.preset.loadingAttribute);
        }
        image.classList.add("is-loaded");
      };

      if (image.complete && image.naturalWidth > 0) {
        markLoaded();
        return;
      }

      if (this.preset.loadingAttribute) {
        image.setAttribute(this.preset.loadingAttribute, "true");
      }
      image.addEventListener("load", markLoaded, {
        once: true,
        signal: this.abortController.signal,
      });
      image.addEventListener("error", markLoaded, {
        once: true,
        signal: this.abortController.signal,
      });
    });
  }

  bindEvents() {
    const signal = this.abortController.signal;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", () => this.open(trigger), { signal });
    });

    this.previousButton?.addEventListener("click", () => this.move(-1), {
      signal,
    });
    this.nextButton?.addEventListener("click", () => this.move(1), { signal });
    this.closeButton?.addEventListener("click", () => this.close(), { signal });
    this.dialog?.addEventListener(
      "pointerdown",
      (event) => this.startDrag(event),
      { signal },
    );
    this.dialog?.addEventListener("pointermove", (event) => this.drag(event), {
      signal,
    });
    this.dialog?.addEventListener("pointerup", () => this.endDrag(), {
      signal,
    });
    this.dialog?.addEventListener("pointercancel", () => this.resetDrag(), {
      signal,
    });
    this.dialog?.addEventListener("lostpointercapture", () => this.endDrag(), {
      signal,
    });
    this.dialog?.addEventListener(
      "click",
      (event) => this.handleDialogClick(event),
      { signal },
    );
    this.dialog?.addEventListener(
      "keydown",
      (event) => this.handleKeydown(event),
      { signal },
    );
    this.dialog?.addEventListener("close", () => this.resetDialog(), {
      signal,
    });
  }

  renderThumbnails() {
    if (!(this.thumbnails instanceof HTMLElement)) return;

    this.thumbnails.replaceChildren();
    // Translated labels come from the dialog's data attributes ([index]/[alt] tokens); English is the fallback.
    const labels = this.dialog?.dataset || {};
    const showMediaLabel = labels.ppZoomShowMediaLabel || "Show media [index]";
    const thumbnailAltLabel = labels.ppZoomThumbnailAltLabel || "[alt] thumbnail";
    const thumbnailFallbackAltLabel =
      labels.ppZoomThumbnailFallbackAltLabel || "Media [index] thumbnail";
    this.items.forEach((item, index) => {
      const thumbnail = document.createElement("button");
      thumbnail.type = "button";
      thumbnail.className = [
        "pp-zoom-dialog__thumbnail",
        this.preset.thumbnailClass,
      ]
        .filter(Boolean)
        .join(" ");
      thumbnail.dataset.ppZoomThumbnail = "";
      thumbnail.setAttribute("aria-label", showMediaLabel.replace("[index]", index + 1));

      if (item.thumb) {
        const thumbnailImage = document.createElement("img");
        thumbnailImage.src = item.thumb;
        thumbnailImage.alt = item.alt
          ? thumbnailAltLabel.replace("[alt]", item.alt)
          : thumbnailFallbackAltLabel.replace("[index]", index + 1);
        thumbnailImage.loading = "lazy";
        thumbnailImage.decoding = "async";
        thumbnail.append(thumbnailImage);
      } else {
        const thumbnailLabel = document.createElement("span");
        thumbnailLabel.textContent =
          item.type === "video" || item.type === "external_video"
            ? "Video"
            : `Media ${index + 1}`;
        thumbnail.append(thumbnailLabel);
      }
      thumbnail.addEventListener("click", () => this.setImage(index), {
        signal: this.abortController.signal,
      });
      this.thumbnails.append(thumbnail);
    });
  }

  updateControls() {
    const hasCarousel = this.items.length > 1;
    this.dialog?.classList.toggle("has-carousel", hasCarousel);

    if (this.previousButton) this.previousButton.hidden = !hasCarousel;
    if (this.nextButton) this.nextButton.hidden = !hasCarousel;
    if (this.thumbnails) this.thumbnails.hidden = !hasCarousel;

    this.thumbnails
      ?.querySelectorAll("[data-pp-zoom-thumbnail]")
      .forEach((thumbnail, index) => {
        const isActive = index === this.activeIndex;
        thumbnail.classList.toggle("is-active", isActive);
        thumbnail.setAttribute("aria-current", isActive ? "true" : "false");
        if (isActive) {
          thumbnail.scrollIntoView({ block: "nearest", inline: "center" });
        }
      });
  }

  markLoaded(loadToken) {
    if (loadToken !== this.loadToken) return;

    this.dialog?.classList.remove("is-loading");
    this.dialog?.removeAttribute("aria-busy");
    this.image?.classList.add("is-loaded");
    this.video?.classList.add("is-loaded");
    this.iframe?.classList.add("is-loaded");

    window.clearTimeout(this.transitionTimer);
    this.transitionTimer = window.setTimeout(() => {
      this.dialog?.classList.remove("is-transitioning");
    }, 520);
  }

  clearMedia() {
    if (this.image instanceof HTMLImageElement) {
      this.image.removeAttribute("src");
      this.image.alt = "";
      this.image.classList.remove("is-loaded");
      this.image.hidden = true;
    }

    if (this.video instanceof HTMLVideoElement) {
      this.video.pause();
      this.video.removeAttribute("src");
      this.video.load();
      this.video.classList.remove("is-loaded");
      this.video.hidden = true;
    }

    if (this.iframe instanceof HTMLIFrameElement) {
      this.iframe.removeAttribute("src");
      this.iframe.classList.remove("is-loaded");
      this.iframe.hidden = true;
    }
  }

  setImage(index) {
    if (!this.dialog || !this.items.length) return;

    const nextIndex = (index + this.items.length) % this.items.length;
    const item = this.items[nextIndex];
    if (!item?.src && !item?.videoSrc && !item?.iframeSrc) return;
    const previousIndex = this.activeIndex;
    const hasPreviousMedia =
      this.activeTrigger !== null ||
      this.image?.classList.contains("is-loaded") ||
      this.video?.classList.contains("is-loaded") ||
      this.iframe?.classList.contains("is-loaded");
    const direction =
      this.transitionDirection || (nextIndex > previousIndex ? 1 : -1);

    this.activeIndex = nextIndex;
    this.activeTrigger = item.trigger;
    this.loadToken += 1;
    const loadToken = this.loadToken;

    this.dialog.classList.add("is-loading");
    this.dialog.setAttribute("aria-busy", "true");
    this.dialog.dataset.ppZoomMediaType = item.type;
    this.updateControls();

    if (
      item.type === "image" &&
      this.image instanceof HTMLImageElement &&
      item.src
    ) {
      this.setImageMedia(item, loadToken, direction, hasPreviousMedia);
      return;
    }

    this.startMediaTransition(direction, hasPreviousMedia);
    this.clearMedia();

    if (
      item.type === "video" &&
      this.video instanceof HTMLVideoElement &&
      item.videoSrc
    ) {
      this.video.hidden = false;
      this.video.src = item.videoSrc;
      this.video.load();
      this.video.addEventListener(
        "loadeddata",
        () => this.markLoaded(loadToken),
        {
          once: true,
          signal: this.abortController.signal,
        },
      );
      this.video.addEventListener("error", () => this.markLoaded(loadToken), {
        once: true,
        signal: this.abortController.signal,
      });
      return;
    }

    if (
      item.type === "external_video" &&
      this.iframe instanceof HTMLIFrameElement &&
      item.iframeSrc
    ) {
      this.iframe.hidden = false;
      this.iframe.src = item.iframeSrc;
      this.iframe.addEventListener("load", () => this.markLoaded(loadToken), {
        once: true,
        signal: this.abortController.signal,
      });
      return;
    }
  }

  setImageMedia(item, loadToken, direction, hasPreviousMedia) {
    if (!(this.image instanceof HTMLImageElement) || !item.src) return;

    const preloadImage = new Image();
    const showImage = () => {
      if (loadToken !== this.loadToken) return;

      this.startMediaTransition(direction, hasPreviousMedia);
      this.video?.pause();
      this.video?.removeAttribute("src");
      this.video?.load();
      if (this.video) {
        this.video.classList.remove("is-loaded");
        this.video.hidden = true;
      }
      if (this.iframe) {
        this.iframe.removeAttribute("src");
        this.iframe.classList.remove("is-loaded");
        this.iframe.hidden = true;
      }

      this.image.classList.remove("is-loaded");
      this.image.alt = item.alt;
      this.image.src = preloadImage.currentSrc || preloadImage.src;
      this.image.hidden = false;

      window.requestAnimationFrame(() => {
        if (loadToken !== this.loadToken) return;
        this.image?.classList.add("is-loaded");
        this.markLoaded(loadToken);
      });
    };

    preloadImage.src = item.src;
    preloadImage.alt = item.alt;

    if (typeof preloadImage.decode === "function") {
      preloadImage.decode().then(showImage).catch(showImage);
    } else if (preloadImage.complete) {
      showImage();
    } else {
      preloadImage.addEventListener("load", showImage, {
        once: true,
        signal: this.abortController.signal,
      });
      preloadImage.addEventListener("error", showImage, {
        once: true,
        signal: this.abortController.signal,
      });
    }
  }

  move(direction) {
    if (this.items.length <= 1) return;
    this.transitionDirection = direction;
    this.setImage(this.activeIndex + direction);
    this.transitionDirection = 0;
  }

  startDrag(event) {
    if (this.items.length <= 1) return;
    if (typeof event.button === "number" && event.button !== 0) return;
    if (
      event.target instanceof Element &&
      event.target.closest(
        "button, a, video, iframe, [data-pp-zoom-thumbnails]",
      )
    )
      return;

    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragDeltaX = 0;
    this.didDrag = false;
    this.getActiveMediaElement()?.classList.add("is-dragging");
    this.dialog?.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  drag(event) {
    const mediaElement = this.getActiveMediaElement();
    if (!this.isDragging || !mediaElement) return;

    this.dragDeltaX = event.clientX - this.dragStartX;
    const dragDeltaY = event.clientY - this.dragStartY;
    if (Math.abs(this.dragDeltaX) < Math.abs(dragDeltaY) * 0.7) return;

    if (Math.abs(this.dragDeltaX) > 6) {
      this.didDrag = true;
    }
    const dragOffset = Math.max(Math.min(this.dragDeltaX * 0.32, 110), -110);
    mediaElement.style.transform = `translateX(${dragOffset}px)`;
    event.preventDefault();
  }

  endDrag() {
    if (!this.isDragging) return;

    const shouldMove =
      Math.abs(this.dragDeltaX) > Math.min(72, window.innerWidth * 0.12);
    const direction = this.dragDeltaX < 0 ? 1 : -1;
    const shouldSuppressClick = this.didDrag;
    this.resetDrag();

    if (shouldSuppressClick) {
      this.suppressClick = true;
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    }
    if (shouldMove) this.move(direction);
  }

  resetDrag() {
    this.isDragging = false;
    this.dragDeltaX = 0;

    const mediaElement = this.getActiveMediaElement();
    if (!mediaElement) return;

    mediaElement.style.transform = "";
    mediaElement.classList.remove("is-dragging");
  }

  open(trigger) {
    if (!this.dialog || !this.items.length) return;

    const triggerIndex = Number.parseInt(
      trigger.dataset.ppZoomItemIndex || "-1",
      10,
    );
    if (!Number.isFinite(triggerIndex) || triggerIndex < 0) return;

    this.setImage(triggerIndex);

    if (typeof this.dialog.showModal === "function") {
      try {
        this.dialog.showModal();
      } catch (_error) {
        this.dialog.setAttribute("open", "");
      }
    } else {
      this.dialog.setAttribute("open", "");
    }
  }

  close() {
    if (!this.dialog) return;

    if (typeof this.dialog.close === "function") {
      this.dialog.close();
      return;
    }

    this.dialog.removeAttribute("open");
    this.resetDialog();
  }

  resetDialog() {
    this.loadToken += 1;
    this.dialog?.classList.remove("is-loading");
    this.dialog?.classList.remove("is-transitioning");
    this.dialog?.removeAttribute("aria-busy");
    this.dialog?.removeAttribute("data-pp-zoom-media-type");
    this.dialog?.removeAttribute("data-pp-zoom-direction");
    window.clearTimeout(this.transitionTimer);
    this.clearMedia();

    if (this.activeTrigger instanceof HTMLElement) {
      this.activeTrigger.focus({ preventScroll: true });
    }
    this.activeTrigger = null;
  }

  getActiveMediaElement() {
    if (this.video instanceof HTMLVideoElement && !this.video.hidden)
      return this.video;
    if (this.iframe instanceof HTMLIFrameElement && !this.iframe.hidden)
      return this.iframe;
    if (this.image instanceof HTMLImageElement && !this.image.hidden)
      return this.image;
    return null;
  }

  handleDialogClick(event) {
    if (this.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.target === this.dialog) {
      this.close();
    }
  }

  startMediaTransition(direction, hasPreviousMedia) {
    if (!this.dialog) return;

    window.clearTimeout(this.transitionTimer);
    this.dialog.dataset.ppZoomDirection = direction < 0 ? "previous" : "next";

    if (hasPreviousMedia) {
      this.dialog.classList.add("is-transitioning");
    }

    this.transitionTimer = window.setTimeout(() => {
      this.dialog?.classList.remove("is-transitioning");
    }, 1800);
  }

  handleKeydown(event) {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.move(-1);
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.move(1);
    }
  }
}

if (!customElements.get("pretty-portal-zoom-dialog")) {
  customElements.define("pretty-portal-zoom-dialog", PrettyPortalZoomDialog);
}
